/**
 * Display formatters shared across pages. The recurring hazard they guard against:
 * Postgres returns `numeric` columns as STRINGS (e.g. "30.00", "0.0500") to preserve
 * precision, and the MCP forwards them verbatim. Formatting one with `.toFixed` directly
 * throws; `Number(string)` is the fix, but it must be done in one place so every page
 * coerces the same way and renders the same fallback for null/blank/invalid values.
 */

/** Coerce the MCP's string-or-number numeric to a finite number, or null when absent,
 *  blank, or unparseable — never NaN, so callers get a clean fallback instead of "NaN". */
export function num(v: number | string | null | undefined): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Tolerant USD format: "$30.00", or `dash` when the value is absent/invalid. */
export function money(v: number | string | null | undefined, dash = '—'): string {
  const n = num(v);
  return n == null ? dash : `$${n.toFixed(2)}`;
}

/** Fraction → percent: 0.05 -> "5.0%". `digits` controls decimal places; `dash` on absent/invalid. */
export function pct(v: number | string | null | undefined, digits = 1, dash = '—'): string {
  const n = num(v);
  return n == null ? dash : `${(n * 100).toFixed(digits)}%`;
}

/** A string value or an em-dash placeholder when it's null/undefined/blank. */
export function orDash(v: string | null | undefined): string {
  return v != null && v !== '' ? v : '—';
}
